import { z } from 'zod';

/**
 * Primitives shared by every other schema in this package.
 *
 * Defined once so that "what counts as a valid jersey number" has exactly one
 * answer, on both sides of the wire (rubric E2).
 */

export const UuidSchema = z.uuid();

/**
 * Email, normalized BEFORE it is validated.
 *
 * Order matters and the obvious spelling gets it wrong. Writing
 * `z.email().trim()` validates the raw input first, so an address pasted out of
 * a text message with a trailing space is rejected as malformed. A parent who
 * pastes their own correct email and is told it is invalid does not try a third
 * time, they text the coach. Trim and lowercase first, then check the shape.
 */
export const EmailSchema = z.string().trim().toLowerCase().pipe(z.email().max(320));

/**
 * Phone. Deliberately permissive.
 *
 * A parent typing "(614) 555-0123" or "614.555.0123" at 9pm has not made an
 * error, and rejecting them costs a completed profile. Normalization to E.164
 * happens on the way to the database, not at the form boundary. Strictness here
 * buys nothing and costs onboarding.
 */
export const PhoneSchema = z
  .string()
  .trim()
  .min(7)
  .max(32)
  .regex(/^[+()\-.\s\d]+$/, 'phone_format');

export const NonEmptyStringSchema = z.string().trim().min(1);
export const PersonNameSchema = z.string().trim().min(1).max(80);

/**
 * Jersey number.
 *
 * Zero through 99 because basketball allows 0 and 00, and a ten-year-old who
 * picked 0 should not be told her number is invalid. 0 and 00 are distinct
 * numbers in the rulebook but not in an integer, so this is stored as an
 * integer and the "00" case is out of scope until someone asks for it.
 * Uniqueness per season is enforced by the database and surfaced as
 * JERSEY_TAKEN, never as a constraint violation (BUILD_PLAN 4.3).
 */
export const JerseyNumberSchema = z.int().min(0).max(99);

/** A calendar date with no time component. Birthdates and season bounds. */
export const DateOnlySchema = z.iso.date();
export const TimestampSchema = z.iso.datetime();

/**
 * A URL-safe slug. Lowercase, no leading or trailing hyphen, no doubles.
 */
export const SlugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(2)
  .max(64)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'slug_format');

/**
 * A hex color as the coach typed it. The derived palette and the WCAG contrast
 * gate live in packages/ui; this only validates the shape.
 */
export const HexColorSchema = z
  .string()
  .trim()
  .regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, 'hex_color');

/** Cursor pagination, per BUILD_PLAN 8.3. Never offset. */
export const PaginationSchema = z.object({
  cursor: z.string().max(256).optional(),
  limit: z.int().min(1).max(100).default(50),
});
export type Pagination = z.infer<typeof PaginationSchema>;

/** The success envelope. `{ data, meta }`, per BUILD_PLAN 8.3. */
export function envelope<T extends z.ZodType>(data: T) {
  return z.object({
    data,
    meta: z
      .object({
        nextCursor: z.string().nullable().optional(),
        total: z.int().optional(),
      })
      .optional(),
  });
}
